import { useQuery } from '@tanstack/react-query';
import { appsApi } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Calculator, LayoutGrid, ExternalLink } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { useCallback } from 'react';
import { QueryState } from '@/components/query-state';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'file-text': FileText,
  'calculator': Calculator,
  default: LayoutGrid,
};

const TOKEN_KEY = 'rf3_access_token';

function LauncherSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-6 w-32 bg-muted rounded" />
            <div className="h-4 w-48 bg-muted rounded" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['launcher-apps'],
    queryFn: () => appsApi.getLauncher(),
  });

  const apps = data?.apps || [];

  // Launch app with token in URL
  const launchApp = useCallback((app: typeof apps[0]) => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      // If no token, just open the app (it will redirect to Hub for login)
      window.open(app.baseUrl, '_blank');
      return;
    }

    // Construct URL with token
    // Apps expect token in query param: ?token=...
    const url = new URL(app.baseUrl);
    url.searchParams.set('token', token);

    // Open app in new tab
    window.open(url.toString(), '_blank');
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <span className="rf3-eyebrow">Your applications</span>
        <h2 className="text-3xl font-bold tracking-tight text-primary mt-1">
          {user?.displayName
            ? `Welcome back, ${user.displayName.split(' ')[0]}!`
            : 'App launcher'}
        </h2>
        <p className="text-muted-foreground">
          Launch any RF3 application you have access to.
        </p>
      </div>

      {user && (
        <div className="text-sm text-muted-foreground">
          Signed in as <span className="font-medium text-foreground">{user.email}</span>
        </div>
      )}

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={apps.length === 0}
        skeleton={<LauncherSkeleton />}
        emptyMessage="No applications available. Contact your administrator for access."
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app) => {
            const Icon = iconMap[app.icon] || iconMap.default;

            return (
              <button
                key={app.id}
                onClick={() => launchApp(app)}
                className="block text-left w-full"
              >
                <Card className="group h-full cursor-pointer transition-all duration-150 hover:border-rf3-amber hover:shadow-[0_4px_16px_rgba(21,44,72,0.10)]">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ background: 'var(--rf3-amber-soft)' }}>
                          <Icon className="h-6 w-6 text-rf3-amber" />
                        </div>
                        <div>
                          <CardTitle className="text-lg text-primary">{app.name}</CardTitle>
                          <CardDescription className="uppercase tracking-[0.5px] text-[11px] font-semibold text-rf3-slate">{app.category}</CardDescription>
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-rf3-amber" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{app.description}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </QueryState>
    </div>
  );
}
